/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useState, useEffect, Fragment, useRef } from "react";
import { API, Auth, graphqlOperation, Storage } from "aws-amplify";
import { createCalibration, updateCalibration } from "src/graphql/mutations";
import { Dialog, Transition } from "@headlessui/react";
import toast from "react-hot-toast";
import { XMarkIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import ja from "date-fns/locale/ja";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

import "react-datepicker/dist/react-datepicker.css";

import { queryTools, queryMakers } from "src/services/AmplifyQuery";

const initialState = {
  managementNumber: "",
  modelNumber: "",
  serialNumber: "",
  expirationDate: "",
  expirationObs: "",
  certificateS3Path: "",
};

/**
 * The AddCalibrationModal component
 *
 * @author Thais Watanabe
 */
const AddCalibrationModal = (props) => {
  const {
    setIsOpen,
    isOpen,
    calibrations,
    setCalibrations,
    selectedCalibration,
    setSelectedCalibration,
  } = props;
  const [formState, setFormState] = useState(initialState);
  const [loggedUser, setLoggedUser] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedMaker, setSelectedMaker] = useState(null);
  const [expDate, setExpDate] = useState(null);
  const [error, setError] = useState(false);

  const [tools, setTools] = useState([]);
  const [makers, setMakers] = useState([]);

  const cancelButtonRef = useRef(null);

  registerLocale("ja", ja);

  /* useEffect */
  useEffect(() => {
    fetchTools();
    fetchMakers();
  }, [tools, makers]);

  /* useEffect */
  useEffect(() => {
    if (selectedCalibration) {
      const selected = selectedCalibration;

      setSelectedTool({
        value: selectedCalibration.tool.id,
        label: selectedCalibration.tool.name,
      });

      setSelectedMaker({
        value: selectedCalibration.maker.id,
        label: selectedCalibration.maker.name,
      });

      if (selectedCalibration.expirationDate) {
        setExpDate(new Date(selectedCalibration.expirationDate));
      }

      if (selectedCalibration.certificateS3Path) {
        setSelectedFile(selectedCalibration.certificateS3Path);
      }

      setFormState(selected);
    }
  }, [selectedCalibration]);

  /* useEffect */
  useEffect(() => {
    Auth.currentUserInfo()
      .then((result) => {
        setLoggedUser(result.username);
      })
      .catch((error) => console.log(error));
  }, []);

  /**
   * fetchTools
   */
  async function fetchTools() {
    queryTools(null, setTools);
  }

  /**
   * fetchMakers
   */
  async function fetchMakers() {
    queryMakers(null, setMakers);
  }

  /**
   * setInput
   * @param {*} key
   * @param {*} value
   */
  function setInput(key, value) {
    setFormState({ ...formState, [key]: value });
  }

  /**
   * submit
   */
  async function submit() {
    var error = false;

    if (
      !formState.managementNumber ||
      !formState.modelNumber ||
      !formState.serialNumber ||
      !selectedTool ||
      !selectedMaker ||
      formState?.expirationDate
    ) {
      setError(true);
      error = true;
    } else {
      setError(false);
      error = false;
    }

    if (!error) {
      if (selectedCalibration) {
        update();
      } else {
        create();
      }
    }
  }

  /**
   * update
   */
  async function update() {
    try {
      selectedCalibration.name = formState.name;
      selectedCalibration.managementNumber = formState.managementNumber;
      selectedCalibration.modelNumber = formState.modelNumber;
      selectedCalibration.serialNumber = formState.serialNumber;
      selectedCalibration.expirationObs = formState.expirationObs;
      selectedCalibration.expirationDate = moment(expDate).isValid()
        ? moment(expDate).format("YYYY-MM-DD")
        : null;

      const inputCalibration = { ...selectedCalibration };
      inputCalibration.toolID = selectedTool.value;
      inputCalibration.makerID = selectedMaker.value;

      delete inputCalibration._deleted;
      delete inputCalibration._lastChangedAt;
      delete inputCalibration.tool;
      delete inputCalibration.maker;

      let s3FilePath;

      if (selectedFile) {
        s3FilePath = `calibration/${inputCalibration.id}_${selectedFile.name}`;
        await fileUpload(s3FilePath);
        inputCalibration.certificateS3Path = s3FilePath;
        selectedCalibration.certificateS3Path = s3FilePath;
      }

      setIsOpen(!isOpen);
      setFormState(initialState);
      setExpDate(null);
      setSelectedTool(null);
      setSelectedMaker(null);
      setSelectedFile(null);
      setSelectedCalibration(null);

      await API.graphql(
        graphqlOperation(updateCalibration, { input: inputCalibration })
      );
      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  /**
   * addCalibration
   */
  async function create() {
    try {
      const inputCalibration = {
        ...formState,
        author: loggedUser,
      };

      inputCalibration.id = uuidv4();

      let s3FilePath;

      if (selectedFile) {
        s3FilePath = `calibration/${inputCalibration.id}_${selectedFile.name}`;
        await fileUpload(s3FilePath);
      }

      inputCalibration.toolID = selectedTool.value;
      inputCalibration.makerID = selectedMaker.value;
      inputCalibration.expirationDate = moment(expDate).isValid()
        ? moment(expDate).format("YYYY-MM-DD")
        : null;
      inputCalibration.certificateS3Path = s3FilePath;
      console.log(inputCalibration);

      const result = await API.graphql(
        graphqlOperation(createCalibration, { input: inputCalibration })
      );

      const newCalibration = result.data.createCalibration;

      setFormState(initialState);
      setExpDate(null);
      setSelectedFile(null);
      setIsOpen(!isOpen);

      setCalibrations([...calibrations, newCalibration]);
      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  async function fileUpload(s3FilePath) {
    Storage.put(s3FilePath, selectedFile)
      .then((result) => {
        try {
          // changePhoto(selectedPhoto);
        } catch (err) {
          console.log(err);
          toast.error("エラーが発生しました");
        }
      })
      .catch((err) => console.log(err));
  }

  /**
   * closeModal
   */
  function closeModal() {
    setIsOpen(!isOpen);
    setFormState(initialState);
    setExpDate(null);
    setSelectedTool(null);
    setSelectedMaker(null);

    setSelectedFile(null);
    setSelectedCalibration(null);
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={closeModal}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  onClick={() => closeModal()}
                >
                  <span className="sr-only">Close</span>
                  {/* <!-- Heroicon name: x --> */}
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <form>
                <div>
                  <div>
                    <h2 className="text-lg leading-6 font-medium text-gray-900">
                      {selectedCalibration
                        ? "機材校正修正"
                        : "新規機材校正登録"}
                    </h2>
                  </div>

                  <div className="grid mt-6 grid-cols-4 gap-x-4 sm:grid-cols-4">
                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="managementNumber"
                        className="block text-sm font-medium text-gray-700"
                      >
                        管理番号 *
                      </label>
                      <input
                        value={formState.managementNumber}
                        onChange={(event) =>
                          setInput("managementNumber", event.target.value)
                        }
                        type="text"
                        name="managementNumber"
                        id="managementNumber"
                        className={`${
                          error && formState.managementNumber.length < 1
                            ? "pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-50"
                            : "focus:ring-teal-500 focus:border-teal-500 border-gray-300"
                        } block w-full shadow-sm sm:text-sm rounded-md`}
                      />
                      {error && formState.managementNumber.length < 1 && (
                        <p className="mt-2 text-sm text-red-600">
                          「管理番号」を入力してください
                        </p>
                      )}
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="toolID"
                        className="block text-sm font-medium text-gray-700"
                      >
                        機材名称 *
                      </label>
                      <Select
                        id="toolID"
                        name="toolID"
                        options={tools.map((obj) => ({
                          value: obj.id,
                          label: obj.name,
                        }))}
                        value={selectedTool}
                        onChange={(event) => setSelectedTool(event)}
                        placeholder="選択してください"
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: "#0D9488",
                          },
                        })}
                        styles={{
                          input: (base) => ({
                            ...base,
                            "input:focus": {
                              boxShadow: "none",
                            },
                          }),
                        }}
                      />
                      {error && formState.toolID.length < 1 && (
                        <p className="mt-2 text-sm text-red-600">
                          「機材名称」を入力してください
                        </p>
                      )}
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="modelNumber"
                        className="block text-sm font-medium text-gray-700"
                      >
                        型式番号 *
                      </label>
                      <input
                        value={formState.modelNumber}
                        onChange={(event) =>
                          setInput("modelNumber", event.target.value)
                        }
                        type="text"
                        name="modelNumber"
                        id="modelNumber"
                        className={`${
                          error && formState.modelNumber.length < 1
                            ? "pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-50"
                            : "focus:ring-teal-500 focus:border-teal-500 border-gray-300"
                        } block w-full shadow-sm sm:text-sm rounded-md`}
                      />
                      {error && formState.modelNumber.length < 1 && (
                        <p className="mt-2 text-sm text-red-600">
                          「型式番号」を入力してください
                        </p>
                      )}
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="makerID"
                        className="block text-sm font-medium text-gray-700"
                      >
                        メーカー *
                      </label>
                      <Select
                        id="makerID"
                        name="makerID"
                        options={makers.map((obj) => ({
                          value: obj.id,
                          label: obj.name,
                        }))}
                        value={selectedMaker}
                        onChange={(event) => setSelectedMaker(event)}
                        placeholder="選択してください"
                        theme={(theme) => ({
                          ...theme,
                          colors: {
                            ...theme.colors,
                            primary: "#0D9488",
                          },
                        })}
                        styles={{
                          input: (base) => ({
                            ...base,
                            "input:focus": {
                              boxShadow: "none",
                            },
                          }),
                        }}
                      />
                      {error && formState.makerID.length < 1 && (
                        <p className="mt-2 text-sm text-red-600">
                          「メーカー」を入力してください
                        </p>
                      )}
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="serialNumber"
                        className="block text-sm font-medium text-gray-700"
                      >
                        シリアル番号 *
                      </label>
                      <input
                        value={formState.serialNumber}
                        onChange={(event) =>
                          setInput("serialNumber", event.target.value)
                        }
                        type="text"
                        name="serialNumber"
                        id="serialNumber"
                        className={`${
                          error && formState.serialNumber.length < 1
                            ? "pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-50"
                            : "focus:ring-teal-500 focus:border-teal-500 border-gray-300"
                        } block w-full shadow-sm sm:text-sm rounded-md`}
                      />
                      {error && formState.serialNumber.length < 1 && (
                        <p className="mt-2 text-sm text-red-600">
                          「シリアル番号」を入力してください
                        </p>
                      )}
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="expirationDate"
                        className="block text-sm font-medium text-gray-700"
                      >
                        校正有効期限
                      </label>
                      <DatePicker
                        selected={expDate}
                        onChange={(date) => setExpDate(date)}
                        dateFormat="yyyy/MM/dd"
                        locale="ja"
                        className="focus:ring-teal-500 focus:border-teal-500 border-gray-300 block w-full shadow-sm sm:text-sm rounded-md"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="py-2 sm:col-span-12">
                      <label
                        htmlFor="cover-photo"
                        className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                      >
                        校正証明書
                      </label>
                      <div className="mt-1 sm:col-span-2 sm:mt-0">
                        <div className="flex max-w-lg justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6">
                          <div className="w-full space-y-1 text-center">
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div className="flex justify-center text-sm text-gray-600">
                              <label
                                htmlFor="certificateS3Path"
                                className="relative cursor-pointer rounded-md bg-white font-medium text-teal-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-teal-500 focus-within:ring-offset-2 hover:text-teal-500"
                              >
                                <span>ファイルをアップロードする</span>
                                <input
                                  id="certificateS3Path"
                                  name="certificateS3Path"
                                  type="file"
                                  className="sr-only"
                                  accept="image/*,.pdf"
                                  onChange={(e) =>
                                    setSelectedFile(e.target.files[0])
                                  }
                                />
                              </label>
                              {/* <p className="pl-1">または、ドラッグアンドドロップします。</p> */}
                            </div>
                            <p className="text-xs text-gray-500">
                              PDF、PNG、JPG、最大10MB
                            </p>
                            {selectedFile && (
                              <dd className="w-full mt-1 text-sm text-gray-900">
                                <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                                  <li className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                                    <div className="w-0 flex-1 flex items-center">
                                      {/* <!-- Heroicon name: paper-clip --> */}
                                      <PaperClipIcon className="flex-shrink-0 h-5 w-5 text-gray-400" />
                                      <span className="ml-2 flex-1 w-0">
                                        {selectedFile.name
                                          ? selectedFile.name
                                          : selectedFile}
                                      </span>
                                    </div>
                                  </li>
                                </ul>
                              </dd>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="expirationObs"
                        className="block text-sm font-medium text-gray-700"
                      >
                        記載
                      </label>
                      <input
                        value={formState.expirationObs}
                        onChange={(event) =>
                          setInput("expirationObs", event.target.value)
                        }
                        type="text"
                        name="expirationObs"
                        id="expirationObs"
                        className="focus:ring-teal-500 focus:border-teal-500 border-gray-300 block w-full shadow-sm sm:text-sm rounded-md"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    onClick={() => submit()}
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:col-start-2 sm:text-sm"
                  >
                    保存する
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => closeModal()}
                  >
                    キャンセル
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddCalibrationModal;
